import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  FormControl,
  Select,
  MenuItem,
  Link,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBitcoin } from '@fortawesome/free-brands-svg-icons';
import { Building2, ChevronDown, ChevronRight } from 'lucide-react'
import { Expand, Minimize } from 'lucide-react-new';
import { useTranslation } from 'react-i18next';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { makeStyles } from 'tss-react/mui';
import { useLocation } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { Link as RouterLink } from 'react-router-dom';
import useSelectedSubAccount from 'src/hooks/useSelectedSubAccount';
import useSideNav from 'src/hooks/useSideNav';

const useStyles = makeStyles()(theme => ({
  selectStyle:{
    '& .MuiSelect-icon':{
      color: "#9999A1",
      top: "unset"
    }
  },
  breadcrumb: {
    position: "absolute",
    zIndex: "5000",
    top: "-40px",
    minWidth: "350px"
  },
  mediumBreadcrumb: {
    position: "absolute",
    zIndex: "5000",
    top: "-20px",
    minWidth: "350px"
  },
  mobileBreadcrumb: {
    position: "absolute",
    top: "-16px",
    minWidth: "350px",
    '& .MuiBreadcrumbs-li .MuiTypography-root': {
      fontSize: "12px"  
    },
    '& .MuiBreadcrumbs-separator': {
      margin: "0"
    }
  },
  breadCrumbs: {

  },
  mobileBreadCrumbs: {
    padding: '0 18px',
  },
  groupNameChip: {
    marginLeft: "16px",
    color: "#CDD5DF",
    background: "var(--Component-colors-Utility-Gray-utility-gray-50, #121926)",
    borderRadius: "9999px",
    borderWidth: "1px",
    padding: "4px 12px",
    border: "1px solid var(--Component-colors-Utility-Gray-utility-gray-200, #364152)",
    fontWeight: "500",
    fontSize: "14px",
    lineHeight: "20px",
    textAlign: "center"
  }
}));

const InfoBar = (props) => {
  const { classes } = useStyles();
  const theme = useTheme()
  const { coin } = props
  const { t }  = useTranslation();
  const { selectedGroup, handleSelectedGroupUpdate, groupDataLoading } = useSelectedGroup();
  const smallMobileDevice = useMediaQuery(theme.breakpoints.down('sm'));
  const mediumMobileDevice = useMediaQuery(theme.breakpoints.down('md'));
  const largerDevice = useMediaQuery(theme.breakpoints.up(1440)) 
  const { loggedInUser } = useAuth();
  const location = useLocation();
  const [ groupNames, setGroupNames]  = useState([]);
  const approvalsView = location.pathname.split('\/')[2] === 'approvals'
  const homeView = location.pathname === "/app/account-overview"
  const newUserView = location.pathname === '/app/user-management'
  const termsView = location.pathname === '/app/help/terms-of-service'
  const securityAndControlsView = location.pathname.includes('security-and-controls');
  const socReportView = location.pathname.includes('soc-report');
  const adminView = location.pathname.includes("admin") && location.pathname.split("/").length < 6;
  const appRedirectionDone = location.pathname !== "/app"
  const multipleGroups = loggedInUser.groups.length > 1
  const subAcctDetailsView = location.pathname.includes("/app/sub-accounts/") && location.pathname.split("/").length > 3
  const adminSubAcctDetailsView = location.pathname.includes('/app/admin/all-sub-accounts') && location.pathname.split("/").length > 5
  const { selectedSubAccount, selectedSubAccountGroup } = useSelectedSubAccount();
  const subAccountName = selectedSubAccount?.split(', ')[0];
  const { fullscreenMode, handleFullscreenModeUpdate } = useSideNav();

  // Exit fullscreen mode when navigating away from account-overview
  useEffect(() => {
    if (fullscreenMode && !homeView) {
      handleFullscreenModeUpdate(false);
    }
  }, [homeView, fullscreenMode, handleFullscreenModeUpdate]);

  /**
   * Handes the event of onchange on groups dropdown. Currently invokes the change state function of the parent
   * @param {*} event onChange
   * @param {*} value
   */
   const handleGroupAutocompleteChange = (event) => {
    //Grabbing value of onChange event
    const {
      target: { value },
    } = event;
    let newGroups = []
      if(!value.length || (value[value.length - 1] === "All Groups")) {
        handleSelectedGroupUpdate(loggedInUser.groups.map(i => {
          return {
            group: {
              id: i.group.id,
              name: i.group.name,
              viewPaymentsPermission: i.defaultSubAccountRole.viewPayments
            }
          }
        }), loggedInUser.groups) //Context
        setGroupNames(loggedInUser.groups.map(groupInfo => groupInfo.group.name).sort()) //State
      }
      else {
        newGroups = value.filter(group => group !== "All Groups") //Removing string value of "All Groups"
        // Find the Group Obj based on Group Name that is selected
        let allGroupObjArr = loggedInUser.groups.filter(g => newGroups.includes(g.group.name)).map(j => {
          return {
            group: {
              id: j.group.id,
              name: j.group.name,
              viewPaymentsPermission: j.defaultSubAccountRole.viewPayments
            }
          }
        })
        handleSelectedGroupUpdate(allGroupObjArr, loggedInUser.groups) //Storing Group Obj (not just Group Name) in Context
        setGroupNames(
          typeof value === 'string' ? value.split(',') : newGroups.length ? newGroups : value,
        )//State value for Dropdown
    }


  };

  useEffect(()=>{
    let sessionGroupsValue = sessionStorage.getItem('selectedGroups') || JSON.stringify([])
    if(JSON.parse(sessionGroupsValue).length) {
      sessionGroupsValue = loggedInUser.groups.filter(i => JSON.parse(sessionGroupsValue).filter(j => i.group.id === j.group.id))
      if(loggedInUser.groups.length === sessionGroupsValue.length){
        let arr = loggedInUser.groups.filter(i => {
          return selectedGroup.some(j => {
            return j.group.id === i.group.id
          })
        })
        setGroupNames(arr.map(groupInfo => groupInfo.group.name).sort())
      }
      else {
        setGroupNames(
          JSON.parse(sessionGroupsValue).map(i => i.group.name).sort()
        )
      }
    }
    else {
      sessionStorage.setItem('selectedGroups', JSON.stringify(loggedInUser.groups.map(i => {
        return {
          group: {
            id: i.group.id,
            name: i.group.name,
            viewPaymentsPermission: i.defaultSubAccountRole.viewPayments
          }
        }
      })))
    }
  }, [loggedInUser])

  useEffect(()=>{
    setGroupNames(loggedInUser.groups.map(groupInfo => groupInfo.group.name).sort())
  }, [])

  const handleTitle = () => {
    switch (location.pathname) {
      case "/app":
        return
      
      case "/app/account-overview":
        return `${t('welcomeBack')}, ${loggedInUser.preferredName.split(" ")[0] || loggedInUser.preferredName}`
      
      case "/app/sub-accounts":
        return t('subAccounts')

      case "/app/reports":
        return t('subAccountReports')

      case "/app/alerts/active":
        return t('alerts')

      case "/app/alerts/manage":
        return t('manageAlerts')

      case "/app/admin/pool-statistics/pool-daily-hashrate":
        return t('poolDailyStatistics')

      case "/app/admin/sub-account-financials":
        return t('allSubAccountFinancials') 
      
      case '/app/admin/group-user':
        return "Group/User Management"
      
      case "/app/admin/pool-statistics/pool-hashrate":
        if(!loggedInUser.adminRole.adminRead){
          return 
        }

      default:
        return t(location.pathname.split("/")[location.pathname.split("/").length - 1].replace(/-([a-z])/g, (x, up) => up.toUpperCase()));
    }
  }

  return (
    <Box style={
      mediumMobileDevice
        ? { position: "absolute",
            top: "68px",
            left: smallMobileDevice
            ? "16px"
            : "24px",
            maxWidth: "calc(100% - 16px)",
            right: smallMobileDevice
            ? "16px"
            : ''
          }
        : {
            position: "absolute",
            top: "68px",
            left: fullscreenMode
                    ? "24px"
                    : homeView || adminView
                      ? largerDevice
                        ? 'calc(50% - 462px)'
                        : "254px"
                      : "286px",
            right: "24px",
            maxWidth: fullscreenMode ? "none" : "1152px",
            transition: "left 410ms cubic-bezier(0, 0, 0.2, 1) 0ms"
          }
        }>
      <header
        style={{minHeight: "63px", display: "flex"}}
        className="has-space-m"
      >
        { (subAcctDetailsView || adminSubAcctDetailsView) && selectedSubAccount !== 'No Sub-Account Selected' && <div className={smallMobileDevice ? classes.mobileBreadcrumb : mediumMobileDevice ? classes.mediumBreadcrumb : classes.breadcrumb}>
          {
            <Breadcrumbs
                separator={<ChevronRight size={20} />}
                aria-label="breadcrumb"
              >
              <Link
                color={"#CDD5DF"}
                component={RouterLink}
                to={"/app/account-overview"}
              >
                <Typography>Home</Typography>
              </Link>    
              <Link
                color={"#CDD5DF"}
                component={RouterLink}
                to={
                  (location.state?.root === t('allSubAccounts')) || (location.pathname.match('all'))
                    ? '/app/admin/all-sub-accounts'
                    : '/app/sub-accounts'
                }
              >
                <Typography>{location.pathname.match('all') && !smallMobileDevice ? t('allSubAccounts') : t('subAccounts')}</Typography>
              </Link>
              <Typography color={"#FFFFFF"} style={{fontWeight: "600"}}>{subAccountName}</Typography>
            </Breadcrumbs> 
          }
        </div>
        }
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
          {(
            <p className="m-heading is-size-xxl">
              {
                (subAcctDetailsView || adminSubAcctDetailsView)
                  ? selectedSubAccount !== 'No Sub-Account Selected'
                    ? subAccountName
                    : null
                  : handleTitle()
              }
            </p>
          )}
          {
             appRedirectionDone && multipleGroups
              ? smallMobileDevice
                ? ( subAcctDetailsView || adminSubAcctDetailsView )
                  ? selectedSubAccount !== 'No Sub-Account Selected' && <Typography className={classes.groupNameChip}>{adminSubAcctDetailsView ? selectedSubAccountGroup : location.state?.group || selectedSubAccountGroup}</Typography>
                  : !approvalsView && !adminView && !newUserView && !termsView && !securityAndControlsView && !socReportView && <FormControl sx={{ m: 1, mt:1.5, ml:2, mr: 0, width: 180 }}>
                   <Select
                     className={classes.selectStyle}
                     sx={{borderRadius: "8px"}}
                     SelectDisplayProps={{ style: { paddingTop: 12, paddingBottom: 12 } }}
                     labelId="multiple-checkbox-label"
                     id="multiple-checkbox"
                     multiple
                     value={groupNames.sort()}
                     onChange={handleGroupAutocompleteChange}
                     input={<OutlinedInput />}
                     renderValue={(selected) => {
                       return selected.join(', ')
                     }}
                     IconComponent={ChevronDown}
                     MenuProps={{
                       PaperProps: {
                         sx: {
                           bgcolor: 'var(--neutral-1)',
                           '& .MuiMenuItem-root': {
                             paddingTop: 1
                           },
                         },
                       },
                     }}
                   >
                     <MenuItem disabled={(groupNames.length === loggedInUser.groups.length) || groupDataLoading} value={"All Groups"}>
                       <ListItemText primary={["All Groups"]} />
                       <Checkbox 
                         checked={JSON.stringify(groupNames) === JSON.stringify(['All Groups']) || groupNames.length === loggedInUser.groups.length}
                         sx={{
                           color: "rgb(128,128,128)",
                           '& .Mui-checked': {
                             color: "var(--color-menu-selected)"
                           }
                         }}
                       />
                     </MenuItem>
                     <Divider />
                       {loggedInUser.groups.map(groupInfo => groupInfo.group.name).sort().map((group) => {
                         return (
                           <MenuItem key={group} value={group} disabled={groupDataLoading}>
                             <ListItemText primary={group} />
                             <Checkbox 
                               checked={(groupNames.indexOf(group) > -1) || JSON.stringify(groupNames) === JSON.stringify(['All Groups']) || groupNames.length === loggedInUser.groups.length}
                               sx={{
                                 color: "rgb(128,128,128)",
                                 '& .Mui-checked': {
                                   color: "var(--color-menu-selected)"
                                 },
                               }}
                             />
                
                           </MenuItem>
                         )
                       }
                     )}
                    </Select>
                  </FormControl> 
                : ( subAcctDetailsView || adminSubAcctDetailsView )
                    ? selectedSubAccount !== 'No Sub-Account Selected' && <Typography className={classes.groupNameChip}>{adminSubAcctDetailsView ? selectedSubAccountGroup : location.state?.group || selectedSubAccountGroup}</Typography>
                    : !approvalsView && !adminView && !newUserView && !termsView && !securityAndControlsView && !socReportView && <FormControl sx={{ m: 1, mt:1.5, ml:2, width: 288, minWidth: 90, maxWidth: 'unset' }}>
                        <Select
                          className={classes.selectStyle}
                          sx={{borderRadius: "8px"}}
                          SelectDisplayProps={{ style: { paddingTop: 10, paddingBottom: 10 } }}
                          labelId="multiple-checkbox-label"
                          id="multiple-checkbox"
                          multiple
                          value={groupNames.sort()}
                          onChange={handleGroupAutocompleteChange}
                          IconComponent={ChevronDown}
                          renderValue={(selected) => {
                            return (
                              <>
                                <Building2 style={{marginRight: 10, position: "relative", top: "2px"}} size={16} color={"#9999A1"}/>
                                {selected.join(', ')}
                              </>
                                
                            )
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                bgcolor: 'var(--neutral-1)',
                                '& .MuiMenuItem-root': {
                                  paddingTop: 1
                                }
                              },
                            },
                          }}
                    
                        >
                        <MenuItem disabled={(groupNames.length === loggedInUser.groups.length) || groupDataLoading} value={"All Groups"}>
                          <ListItemText primary={["All Groups"]} />
                          <Checkbox 
                            checked={JSON.stringify(groupNames) === JSON.stringify(['All Groups']) || groupNames.length === loggedInUser.groups.length} 
                            disabled={groupDataLoading}
                            sx={{
                              color: "rgb(128,128,128)",
                              '& .Mui-checked': {
                                color: "var(--color-menu-selected)"
                              },
                            }}
                          />
                        </MenuItem>
                        <Divider />
                        {loggedInUser.groups.map(groupInfo => groupInfo.group.name).sort().map((group) => {
                          return (
                            <MenuItem key={group} value={group} disabled={groupDataLoading}>
                              <ListItemText primary={group} />
                              <Checkbox 
                                checked={(groupNames.indexOf(group) > -1 || (JSON.stringify(groupNames) === JSON.stringify(['All Groups']) || groupNames.length === loggedInUser.groups.length))}
                                sx={{
                                  color: "rgb(128,128,128)",
                                  '& .Mui-checked': {
                                    color: "var(--color-menu-selected)"
                                  },
                                }}
                              />
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </FormControl> 
              : appRedirectionDone && !multipleGroups && !adminView ?
              <Typography className={classes.groupNameChip}>{adminSubAcctDetailsView ? selectedSubAccountGroup : loggedInUser.groups[0]?.group?.name}</Typography>
            : null
          }
          <div style={{flex: 1}}></div>
          {!mediumMobileDevice && homeView && (
            <Box
              onClick={() => handleFullscreenModeUpdate(!fullscreenMode)}
              sx={{
                padding: '8px 12px',
                backgroundColor: 'var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-bg, #121926)',
                border: '1px solid var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#1a2332',
                }
              }}
            >
              {fullscreenMode ? (
                <Minimize size={20} color="#e6e5e8" />
              ) : (
                <>
                  <Expand size={20} color="#e6e5e8" />
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#e6e5e8' }}>
                    Fullscreen
                  </Typography>
                </>
              )}
            </Box>
          )}
        </div>
      </header>
    </Box>
  );
};

export default InfoBar;